import React, { useState } from "react";
import { Typography } from "antd";
import { SbButton, SbModal, SbCheckbox, SbTooltip, SbForm, SbInput } from "uiCore";
import useTalentMobilitySettingsStore from "Store/useTalentMobilitySettingsStore";
import { DEFAULT_NOTIFICATIONS } from "Store/useRedeploymentPlanStore";

const { Text } = Typography;

const SHORTLIST_REASON_MAX_LENGTH = 1000;

// Copy per mode — "create" confirms a new shortlist, "recommend" confirms a
// Candidate Profile recommendation for one specific next role, "edit"
// updates the notify choices / reason of a shortlist that already exists.
const MODE_COPY = {
  create: {
    title: "Shortlist this candidate for redeployment?",
    description: ({ personName }) =>
      `${personName} will be shortlisted for redeployment. Selected notifications will be sent automatically as soon as the plan is approved.`,
    confirmLabel: "Shortlist for Redeployment",
  },
  recommend: {
    title: "Recommend this candidate for this role?",
    description: ({ personName, roleTitle }) =>
      `${personName} will be recommended${roleTitle ? ` for ${roleTitle}` : ""}.`,
    confirmLabel: "Recommend for this role",
  },
  edit: {
    title: "Edit shortlist details",
    description: ({ personName }) =>
      `Update the notifications and reason for ${personName}'s redeployment shortlist. Selected notifications will be sent automatically as soon as the plan is approved.`,
    confirmLabel: "Save Changes",
  },
};

const toFormState = (initialValues) => ({
  notifications: { ...DEFAULT_NOTIFICATIONS, ...initialValues?.notifications },
  reason: initialValues?.reason ?? "",
});

// The redeployment shortlist dialog shared by both shortlist buttons —
// Employee View's PersonAvailabilityButton and Reduction Detail's
// ShortlistAvailabilityButton — plus the Candidate Profile's "Recommend for
// this role" (mode="recommend", roleTitle names the role) and Reduction
// Detail's "Reason" cell Edit action (mode="edit"), so the notify checkboxes
// and the Reason for Shortlisting field look and validate the same
// everywhere. Callers own what a confirm does to the plan draft; this owns
// only the form.
//
// The Reason for Shortlisting field is mandatory (requireReason, on by
// default) — the confirm button stays disabled until it holds non-blank
// text. It's turned off for At Risk employees in the target-first flow:
// Reduction Detail's At Risk rows, and the Candidate Profile's Recommend when
// opened from them — they're already below the cutoff, so shortlisting them
// needs no justification.
// initialValues ({ notifications, reason }) pre-fills the form each time the
// dialog opens (edit, or a recommend reusing saved notify choices); without
// it the form opens blank. onConfirm
// receives { notifications, reason }, notifications in the plan entry's
// stored shape, reason null when the field isn't shown.
const ShortlistConfirmModal = ({
  open,
  mode = "create",
  requireReason = true,
  personName,
  roleTitle,
  initialValues,
  onCancel,
  onConfirm,
}) => {
  const copy = MODE_COPY[mode];
  const notificationsEnabled = useTalentMobilitySettingsStore((state) => state.matchNotificationsEnabled);
  const [form, setForm] = useState(() => toFormState(initialValues));

  // Re-seed the form on every opening (React's "adjust state when a prop
  // changes" pattern, not an effect), so a new shortlist always starts blank,
  // an edit always starts from the saved values, and a cancelled half-typed
  // reason never carries over.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setForm(toFormState(initialValues));
  }

  const isReasonMissing = requireReason && !form.reason.trim();

  const setNotification = (key) => (e) =>
    setForm((prev) => ({ ...prev, notifications: { ...prev.notifications, [key]: e.target.checked } }));

  const handleConfirm = () => {
    if (isReasonMissing) return;
    onConfirm({ notifications: form.notifications, reason: requireReason ? form.reason : null });
  };

  return (
    <SbModal
      open={open}
      title={copy.title}
      onCancel={onCancel}
      destroyOnHidden
      footer={[
        <SbButton key="cancel" buttonType="secondary" onClick={onCancel}>
          Cancel
        </SbButton>,
        <SbButton key="confirm" buttonType="primary" disabled={isReasonMissing} onClick={handleConfirm}>
          {copy.confirmLabel}
        </SbButton>,
      ]}
    >
      <Text>{copy.description({ personName, roleTitle })}</Text>
      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        <SbTooltip content={notificationsEnabled ? "" : "Match notifications are disabled in Admin Settings."}>
          <SbCheckbox
            checked={form.notifications.employee}
            disabled={!notificationsEnabled}
            onChange={setNotification("employee")}
          >
            Notify Employee
          </SbCheckbox>
        </SbTooltip>
        <SbCheckbox checked={form.notifications.line_manager} onChange={setNotification("line_manager")}>
          Notify Line Manager
        </SbCheckbox>
        <SbCheckbox checked={form.notifications.hr_manager} onChange={setNotification("hr_manager")}>
          Notify Hiring Manager
        </SbCheckbox>
      </div>
      {requireReason && (
        <SbForm layout="vertical" style={{ marginTop: 14 }}>
          {/* Bottom margin leaves room for the textarea's character count,
              which antd renders below the box — without it the count crowds
              the modal footer, since this is the last field. */}
          <SbForm.Item label="Reason for Shortlisting" required style={{ marginBottom: 20 }}>
            <SbInput
              textarea
              rows={3}
              autoFocus
              value={form.reason}
              maxLength={SHORTLIST_REASON_MAX_LENGTH}
              showCount
              placeholder="Why is this employee being shortlisted for redeployment?"
              onValueChange={(value) => setForm((prev) => ({ ...prev, reason: value }))}
            />
          </SbForm.Item>
        </SbForm>
      )}
    </SbModal>
  );
};

export default ShortlistConfirmModal;
